/* 

Create a feature flag system that:
- can be turned on or off via query param
- is persistent across SPAs
- is accessible to design-system consumers

// Task 1: create API for reading setting
// Task 2: Read from query param at import time and set initial value
// Task 3: Store initial value in a cookie property
// Task 4: if no query param, read from cookie property


- if value in query param, return value from query param
- if value in cookie, return value from cookie
- return default value

// What it enables in this version: easy to test internally with the feature flag system, rolling out/back is not in scope

How it works:

my.babbel.com/dashboard => default rebrand value, not rebranded
my.babbel.com/dashboard?babbel_rebranding_2024=true => rebranded, and store rebranded in cookie property
my.babbel.com/dashboard?babbel_rebranding_2024=false => not rebranded, and store rebranded in cookie property

*/

export const REBRANDING_QUERY_PARAM = 'babbel_rebranding_2024';
const REBRANDED_DEFAULT_VALUE = false;
const isBrowser = typeof window !== 'undefined';

export type QueryParamRebrandingValue = 'true' | 'false' | undefined;

export const stringFlagToEnum = (
  stringValue: string | null | undefined
): QueryParamRebrandingValue => {
  switch (stringValue) {
    case 'true':
      return 'true';
    case 'false':
      return 'false';
    default:
      return undefined;
  }
};

const readRebrandedQueryParam = () => {
  if (!isBrowser) {
    return undefined;
  }
  // TODO: won't work with SSR
  const originalIsRebrandedFromQueryParam = new URL(window.location.href).searchParams.get(
    REBRANDING_QUERY_PARAM
  );
  return stringFlagToEnum(originalIsRebrandedFromQueryParam);
};

const getRebrandedFromStorage = () => {
  if (!isBrowser) {
    return undefined;
  }
  const regexpMatch = new RegExp(`${REBRANDING_QUERY_PARAM}=(true|false)`).exec(document.cookie);
  const currentValue = regexpMatch?.[1];
  return stringFlagToEnum(currentValue);
};

const getCookieMainDomain = () => {
  const { hostname } = window.location;
  if (hostname === 'localhost') {
    return 'localhost';
  }
  return hostname.endsWith('.cn') ? 'babbel.cn' : 'babbel.com';
};

const storeRebrandedQueryParam = (
  currentStoredValue: QueryParamRebrandingValue,
  isRebrandedFromQueryParam: QueryParamRebrandingValue
) => {
  if (isBrowser && isRebrandedFromQueryParam !== undefined) {
    if (currentStoredValue !== isRebrandedFromQueryParam) {
      const mainDomain = getCookieMainDomain();
      const yearInSeconds = 60 * 60 * 24 * 365;
      document.cookie = `${REBRANDING_QUERY_PARAM}=${isRebrandedFromQueryParam}; Domain=${mainDomain}; Max-Age=${yearInSeconds}; Path=/; SameSite=strict; Secure`;
    }
  }
};

export const getIsRebranded = (): boolean => {
  const isRebrandedFromQueryParam = readRebrandedQueryParam();
  const isRebrandedFromStorage = getRebrandedFromStorage();

  storeRebrandedQueryParam(isRebrandedFromStorage, isRebrandedFromQueryParam);

  if (isRebrandedFromQueryParam !== undefined) {
    return isRebrandedFromQueryParam === 'true';
  }
  if (isRebrandedFromStorage !== undefined) {
    return isRebrandedFromStorage === 'true';
  }
  return REBRANDED_DEFAULT_VALUE;
};

const isRebranded = getIsRebranded();

export { isRebranded };
